import { useEffect, useState } from "react";
import { adminApi } from "@/admin/AdminAuthContext";

const BLANK_POST = { title: "", slug: "", excerpt: "", content: "", cover_image: "", category: "", tags: "", author: "Gopi Gadhvi Group", status: "draft", featured: false, seo_title: "", seo_description: "" };

// Pure helpers extracted from the save() handler so the CRUD hook below
// stays a thin orchestrator over independently-readable transformations.
const parseTagsInput = (tagsInput) => tagsInput.split(",").map((t) => t.trim()).filter(Boolean);

const buildBlogPayload = (editing) => {
  const payload = { ...editing, tags: parseTagsInput(editing.tags) };
  delete payload.id;
  delete payload.created_at;
  delete payload.updated_at;
  delete payload.published_at;
  return payload;
};

/**
 * Owns all Blog CRUD state and API calls for the Admin Blog page --
 * separated from AdminBlog.js so that file only wires state to JSX,
 * per the "separate UI rendering from business logic" pattern already
 * used for the public Contact/Appointment forms (useContactForm.js).
 */
export const useAdminBlogPosts = () => {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => adminApi.get("/blog").then((res) => setPosts(res.data)).finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => setEditing({ ...BLANK_POST });
  const openEdit = (post) => setEditing({ ...post, tags: (post.tags || []).join(", ") });
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    const payload = buildBlogPayload(editing);
    if (editing.id) {
      await adminApi.put(`/blog/${editing.id}`, payload);
    } else {
      await adminApi.post("/blog", payload);
    }
    close();
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    await adminApi.delete(`/blog/${id}`);
    load();
  };

  const updateEditingField = (field, value) => setEditing((prev) => ({ ...prev, [field]: value }));

  return {
    posts,
    editing,
    loading,
    openNew,
    openEdit,
    close,
    save,
    remove,
    updateEditingField,
  };
};
